import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { joinWaitlist } from "../services/waitlist";

interface WaitlistModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const WaitlistModal: React.FC<WaitlistModalProps> = ({ isOpen, onClose }) => {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    if (!isOpen) return null;

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            await joinWaitlist(email.trim());
            setEmail("");
            onClose(); // Close modal before navigating
            navigate('/thanks');
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to join the waitlist. Please try again.";
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative z-50 w-full max-w-lg mx-4 bg-background rounded-3xl shadow-2xl border border-white/10 overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-5 duration-300">
                {/* Decorative radial gradient */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-64 bg-emerald-500/10 blur-3xl -z-10" />

                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 p-2 rounded-full bg-black/5 hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/20 text-muted-foreground transition-colors focus:outline-none z-20"
                >
                    <X className="h-5 w-5" />
                </button>

                <div className="flex flex-col h-full">
                    <div className="flex-1 p-8 md:p-12 flex flex-col items-center text-center">
                        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 text-white shadow-lg shadow-emerald-500/30">
                            <span className="text-2xl font-bold tracking-tighter">SL</span>
                        </div>

                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-3">
                            Reduce <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-500">LLM cost</span> from today
                        </h2>

                        <p className="text-lg text-muted-foreground max-w-lg mx-auto mb-10 leading-relaxed">
                            Experience intelligent routing, semantic caching, and up to 90% cost savings. Secure your spot in the private beta.
                        </p>

                        <form onSubmit={handleSubmit} className="w-full max-w-[420px] space-y-5">
                            <div className="relative group text-left">
                                <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl opacity-20 group-hover:opacity-40 transition duration-500 blur"></div>
                                <Input
                                    type="email"
                                    placeholder="work@company.com"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="relative h-14 px-6 rounded-xl border-border bg-background/90 backdrop-blur text-lg shadow-sm focus-visible:ring-emerald-500 transition-all placeholder:text-muted-foreground/70"
                                />
                            </div>

                            {error && (
                                <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-500 font-medium animate-in fade-in slide-in-from-top-1 text-left">
                                    {error}
                                </div>
                            )}

                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="w-full h-14 rounded-xl text-lg font-bold bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/25 transition-all hover:scale-[1.02] active:scale-[0.98]"
                            >
                                {isLoading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        joining...
                                    </span>
                                ) : (
                                    "Get Early Access"
                                )}
                            </Button>

                            <p className="text-xs text-muted-foreground mt-4 font-medium opacity-80">
                                Limited spots available. No credit card required.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WaitlistModal;
